import { createContext, useContext, ReactNode, useState } from 'react';

export type ViewAsRole = 'admin' | 'marketing' | 'user' | null; // null = no impersonation

interface ImpersonateContextValue {
  viewAsRole: ViewAsRole;
  setViewAsRole: (role: ViewAsRole) => void;
  isImpersonating: boolean;
  effectiveIsAdmin: boolean;
  effectiveIsMarketing: boolean;
}

const ImpersonateContext = createContext<ImpersonateContextValue>({
  viewAsRole: null,
  setViewAsRole: () => {},
  isImpersonating: false,
  effectiveIsAdmin: false,
  effectiveIsMarketing: false,
});

export function ImpersonateProvider({
  children,
  realIsAdmin,
  realIsMarketing,
}: {
  children: ReactNode;
  realIsAdmin: boolean;
  realIsMarketing: boolean;
}) {
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole>(() => {
    const stored = localStorage.getItem('impersonateRole');
    return stored && ['admin', 'marketing', 'user'].includes(stored) ? stored as ViewAsRole : null;
  });

  const setViewAsRole = (role: ViewAsRole) => {
    setViewAsRoleState(role);
    if (role) {
      localStorage.setItem('impersonateRole', role);
    } else {
      localStorage.removeItem('impersonateRole');
    }
  };

  const isImpersonating = viewAsRole !== null;

  const effectiveIsAdmin = isImpersonating ? viewAsRole === 'admin' : realIsAdmin;
  const effectiveIsMarketing = isImpersonating
    ? viewAsRole === 'admin' || viewAsRole === 'marketing'
    : realIsMarketing;

  return (
    <ImpersonateContext.Provider
      value={{ viewAsRole, setViewAsRole, isImpersonating, effectiveIsAdmin, effectiveIsMarketing }}
    >
      {children}
    </ImpersonateContext.Provider>
  );
}

export function useImpersonate() {
  return useContext(ImpersonateContext);
}
